/**
 * Token counting and file size utilities for managing LLM context limits
 */

import { logger } from "./Logger";

// **CONFIGURABLE THRESHOLDS** - Easily adjustable as needed
export const FILE_SIZE_THRESHOLDS = {
  // If file is larger than these thresholds, use snippet extraction instead of full content
  MAX_TOKENS: 30000,
  MAX_BYTES: 150 * 1024, // 150KB

  // Number of lines to extract around search results
  CONTEXT_LINES_ABOVE: 50,
  CONTEXT_LINES_BELOW: 50,

  // Maximum total tokens to send to LLM API (frontend guard)
  MAX_API_TOKENS: 300000,
};

/**
 * Estimates the number of tokens in a text string.
 * Uses a simple approximation: 1 token ≈ 4 characters for most text.
 * This is a conservative estimate that works well for most use cases.
 */
export function estimateTokenCount(text: string): number {
  if (!text || typeof text !== "string") {
    return 0;
  }

  // More accurate estimation considering:
  // - Whitespace and punctuation tend to be separate tokens
  // - Average word length in code is different from natural language
  const characters = text.length;
  const words = text.split(/\s+/).filter(Boolean).length;

  // Use a hybrid approach: base on characters but account for word boundaries
  const tokenEstimate = Math.ceil(characters / 3.5); // Slightly more conservative than 4

  return tokenEstimate;
}

/**
 * Gets the byte size of a text string
 */
export function getByteSize(text: string): number {
  if (!text || typeof text !== "string") {
    return 0;
  }

  return new TextEncoder().encode(text).length;
}

/**
 * Checks if a file exceeds the size thresholds for full content inclusion
 */
export function exceedsThresholds(content: string): boolean {
  if (!content || typeof content !== "string") {
    return false;
  }

  const tokenCount = estimateTokenCount(content);
  const byteSize = getByteSize(content);

  const exceedsTokenLimit = tokenCount > FILE_SIZE_THRESHOLDS.MAX_TOKENS;
  const exceedsByteLimit = byteSize > FILE_SIZE_THRESHOLDS.MAX_BYTES;

  // logger.debug.info(
  //   `File size check: ${tokenCount} tokens, ${byteSize} bytes. ` +
  //     `Exceeds limits: tokens=${exceedsTokenLimit}, bytes=${exceedsByteLimit}`
  // );

  return exceedsTokenLimit || exceedsByteLimit;
}

/**
 * Extracts lines around search matches with line numbers
 */
export function extractSnippetAroundMatches(
  content: string,
  searchTerms: string[],
  contextLinesAbove: number = FILE_SIZE_THRESHOLDS.CONTEXT_LINES_ABOVE,
  contextLinesBelow: number = FILE_SIZE_THRESHOLDS.CONTEXT_LINES_BELOW
): string {
  if (!content || !searchTerms || searchTerms.length === 0) {
    return content;
  }

  const lines = content.split("\n");
  const matchingLineNumbers = new Set<number>();

  // Find all lines that contain any of the search terms (case-insensitive)
  searchTerms.forEach((term) => {
    const lowerTerm = term.toLowerCase();
    lines.forEach((line, index) => {
      if (line.toLowerCase().includes(lowerTerm)) {
        matchingLineNumbers.add(index);
      }
    });
  });

  if (matchingLineNumbers.size === 0) {
    // No matches found, return first portion of file with line numbers
    const maxLines = contextLinesAbove + contextLinesBelow;
    const selectedLines = lines.slice(0, maxLines);
    return selectedLines
      .map((line, index) => `${index + 1}: ${line}`)
      .join("\n");
  }

  // Calculate ranges to include around each match
  const includedLines = new Set<number>();

  matchingLineNumbers.forEach((matchLine) => {
    const startLine = Math.max(0, matchLine - contextLinesAbove);
    const endLine = Math.min(lines.length - 1, matchLine + contextLinesBelow);

    for (let i = startLine; i <= endLine; i++) {
      includedLines.add(i);
    }
  });

  // Convert to sorted array and extract lines with line numbers
  const sortedIncludedLines = Array.from(includedLines).sort((a, b) => a - b);
  const extractedLines: string[] = [];

  let prevLine = -2; // Initialize to ensure first line doesn't get a separator

  sortedIncludedLines.forEach((lineNum) => {
    // Add separator if there's a gap in line numbers
    if (lineNum > prevLine + 1) {
      extractedLines.push("...");
    }

    // Add the line with line number
    extractedLines.push(`${lineNum + 1}: ${lines[lineNum]}`);
    prevLine = lineNum;
  });

  const result = extractedLines.join("\n");

  logger.debug.info(
    `Extracted snippet: ${matchingLineNumbers.size} matches found, ` +
      `${sortedIncludedLines.length} lines included (${estimateTokenCount(
        result
      )} tokens)`
  );

  return result;
}

/**
 * Creates file metadata with size information
 */
export interface FileMetadata {
  tokenCount: number;
  byteSize: number;
  exceedsThresholds: boolean;
  lastUpdated: number;
}

export function createFileMetadata(content: string): FileMetadata {
  const tokenCount = estimateTokenCount(content);
  const byteSize = getByteSize(content);
  const exceedsLimits = exceedsThresholds(content);

  return {
    tokenCount,
    byteSize,
    exceedsThresholds: exceedsLimits,
    lastUpdated: Date.now(),
  };
}

/**
 * Calculates total token count for an array of content strings
 */
export function calculateTotalTokens(contents: string[]): number {
  return contents.reduce((total, content) => {
    return total + estimateTokenCount(content);
  }, 0);
}
